// 分享卡片图片生成（Product / Funding / Channel 三 grid 共用）
//
// 原三处 createCardWrapper / generateImageFromCard / generateCardWithShareInfo 几乎逐行相同，
// 仅卡片宽度、品牌文案、域名、下载文件名、GA 事件名不同。统一为纯 DOM 函数，差异通过 options 参数化。
//
// 顺带修复的历史问题：
//   - 分享图品牌域名统一为 hub.bestreader.ai（FundingGrid 曾漏 hub. 前缀）
//   - 品牌文案统一走 i18n（ProductGrid 曾硬编码 "AI灵感库"）
//   - 移除 FundingGrid 残留的 console.log
//   - 截图延迟统一为 400ms（Funding 50 / Channel 0 均偏短，易截到未渲染图片）
//   - GA 下载事件名统一为 download_<type> / share_download_<type>，消除 Product 与 Channel 同名冲突
//   - toPng 增加 skipFonts:true，绕开 html-to-image 读取跨域 Google Fonts cssRules 抛出的 SecurityError
//   - Font Awesome 字体图标改为内联 SVG（见 FA_ICON_SVGS / replaceFontAwesomeIcons）：skipFonts:true 同时跳过了 FA 字体，
//       导致导出图里图标（日期 fa-calendar-alt、分享按钮 fa-share-alt）变空白方块；截图前把 <i> 替换为内联 SVG 绕过字体依赖
//   - 不再用 transform: scale(2) + width/height:offsetWidth*2 放大截图区：
//       旧实现会把 wrapper 的视觉盒放大到 1680×4H（transform-origin: top-left），
//       而 border-radius 是按视觉盒四个角来裁切的；html-to-image 实际只截 layout 范围
//       (offsetWidth×2)，只剩左上角 (0,0) 落在区域内，其余三个圆角被裁掉，导致导出图
//       只有左上角是圆角、其他三个角呈直角。改为 html-to-image 的 pixelRatio:2 提升
//       canvas 分辨率，layout 保持原 420×H 不变，border-radius 在 layout 边界内完整生效。

import * as htmlToImage from 'html-to-image';

export const SHARE_DOMAIN = 'hub.bestreader.ai';

// —— 分享卡内 Font Awesome 图标 → 内联 SVG ——
// 根因：html-to-image 开启 skipFonts:true（绕开跨域 Google Fonts 的 SecurityError，
// 见 toPng 调用处）后不再内联任何 Web 字体；而 FA 图标是字体图标（::before + 字体文件），
// 因此导出图里这些图标会变成空白方块（页面里正常，是因为浏览器正常加载了 FA 字体）。
// 解决：截图前把卡片内会用到的 FA <i> 直接替换成内联 SVG，彻底不依赖字体文件。
// 仅在此列出分享卡实际出现的图标（日期图标 + 分享按钮），SVG path 取自 @fortawesome/fontawesome-free 6.6.0。
const FA_ICON_SVGS = {
  // fa-calendar-alt（FA6 别名，对应 calendar-days）—— 发布日期前的日历图标
  'fa-calendar-alt': {
    viewBox: '0 0 448 512',
    path: 'M128 0c17.7 0 32 14.3 32 32l0 32 128 0 0-32c0-17.7 14.3-32 32-32s32 14.3 32 32l0 32 48 0c26.5 0 48 21.5 48 48l0 48L0 160l0-48C0 85.5 21.5 64 48 64l48 0 0-32c0-17.7 14.3-32 32-32zM0 192l448 0 0 272c0 26.5-21.5 48-48 48L48 512c-26.5 0-48-21.5-48-48L0 192zm64 80l0 32c0 8.8 7.2 16 16 16l32 0c8.8 0 16-7.2 16-16l0-32c0-8.8-7.2-16-16-16l-32 0c-8.8 0-16 7.2-16 16zm128 0l0 32c0 8.8 7.2 16 16 16l32 0c8.8 0 16-7.2 16-16l0-32c0-8.8-7.2-16-16-16l-32 0c-8.8 0-16 7.2-16 16zm144-16c-8.8 0-16 7.2-16 16l0 32c0 8.8 7.2 16 16 16l32 0c8.8 0 16-7.2 16-16l0-32c0-8.8-7.2-16-16-16l-32 0zM64 400l0 32c0 8.8 7.2 16 16 16l32 0c8.8 0 16-7.2 16-16l0-32c0-8.8-7.2-16-16-16l-32 0c-8.8 0-16 7.2-16 16zm144-16c-8.8 0-16 7.2-16 16l0 32c0 8.8 7.2 16 16 16l32 0c8.8 0 16-7.2 16-16l0-32c0-8.8-7.2-16-16-16l-32 0zm112 16l0 32c0 8.8 7.2 16 16 16l32 0c8.8 0 16-7.2 16-16l0-32c0-8.8-7.2-16-16-16l-32 0c-8.8 0-16 7.2-16 16z',
  },
  // fa-share-alt（FA6 别名，对应 share）—— 卡片右上角分享按钮
  'fa-share-alt': {
    viewBox: '0 0 512 512',
    path: 'M307 34.8c-11.5 5.1-19 16.6-19 29.2l0 64-112 0C78.8 128 0 206.8 0 304C0 417.3 81.5 467.9 100.2 478.1c2.5 1.4 5.3 1.9 8.1 1.9c10.9 0 19.7-8.9 19.7-19.7c0-7.5-4.3-14.4-9.8-19.5C108.8 431.9 96 414.4 96 384c0-53 43-96 96-96l96 0 0 64c0 12.6 7.4 24.1 19 29.2s25 3 34.4-5.4l160-144c6.7-6.1 10.6-14.7 10.6-23.8s-3.8-17.7-10.6-23.8l-160-144c-9.4-8.5-22.9-10.6-34.4-5.4z',
  },
};

// 遍历 rootEl 内所有 FA 图标 <i class="fas/fab/far/fal fa-xxx">，
// 命中映射的替换为内联 SVG；未命中的保持原样（分享卡内不会出现）。
function replaceFontAwesomeIcons(rootEl) {
  rootEl.querySelectorAll('i.fas, i.fab, i.far, i.fal').forEach((icon) => {
    const cls = [...icon.classList].find((c) => FA_ICON_SVGS[c]);
    if (!cls) return;
    const { viewBox, path } = FA_ICON_SVGS[cls];
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', viewBox);
    svg.setAttribute('width', '1em');
    svg.setAttribute('height', '1em');
    svg.setAttribute('fill', 'currentColor');
    svg.setAttribute('aria-hidden', 'true');
    svg.style.display = 'inline-block';
    svg.style.verticalAlign = '-0.125em';
    // 保留原 <i> 的类（如 featured-date-icon / mr-1）与内联样式，
    // 使颜色（currentColor）、字号（1em 对应 font-size）、间距与页面一致。
    icon.classList.forEach((c) => svg.classList.add(c));
    if (icon.style.cssText) svg.style.cssText += icon.style.cssText;
    const pathEl = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    pathEl.setAttribute('d', path);
    svg.appendChild(pathEl);
    icon.parentNode.replaceChild(svg, icon);
  });
}

// 构建「带分享信息」的卡片区块（含分享人/语 + 引号水印），不负责挂载
function buildShareInfo(wrapperDiv, shareData) {
  const shareInfoDiv = document.createElement('div');
  shareInfoDiv.style.marginTop = '20px';
  shareInfoDiv.style.fontSize = '16px';
  shareInfoDiv.style.color = '#4b5563';
  shareInfoDiv.style.backgroundColor = '#ffffff';
  shareInfoDiv.style.padding = '20px';
  shareInfoDiv.style.borderRadius = '10px';
  shareInfoDiv.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.1)';

  let html = '';
  if (shareData.sharer && shareData.sharer.trim() !== '') {
    html += `<p style="font-size: 12px; color: #6b7280;">${shareData.sharer}：</p>`;
  }
  html += `<p style="font-size: 14px; color: #374151;">${shareData.message}</p>`;
  shareInfoDiv.innerHTML = html;

  // 插入到 footer 之前（footer 为 wrapperDiv 最后一个子节点）
  const footerDiv = wrapperDiv.lastChild;
  wrapperDiv.insertBefore(shareInfoDiv, footerDiv);

  shareInfoDiv.style.position = 'relative';

  const quoteIcon = document.createElement('i');
  quoteIcon.className = 'fas fa-quote-left';
  quoteIcon.style.position = 'absolute';
  quoteIcon.style.top = '-22px';
  quoteIcon.style.left = '-10px';
  quoteIcon.style.fontSize = '50px';
  quoteIcon.style.color = 'rgba(0, 0, 0, 0.1)';
  shareInfoDiv.appendChild(quoteIcon);
}

// 克隆真实卡片 DOM，包一层带品牌 footer 的 wrapper，用于截图
export function createCardWrapper(cardEl, { width = 420, brandText = 'AI灵感库', domain = SHARE_DOMAIN } = {}) {
  const wrapperDiv = document.createElement('div');
  wrapperDiv.style.backgroundColor = 'rgb(246, 239, 225)'; // 暖调黄铜底色（不透明）
  wrapperDiv.style.padding = '20px';
  wrapperDiv.style.display = 'inline-block';
  wrapperDiv.style.width = `${width}px`;
  wrapperDiv.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.1)';
  wrapperDiv.style.borderRadius = '10px';

  const clonedCard = cardEl.cloneNode(true);
  // 移除左右箭头按钮（多图轮播专用，分享图无需）
  clonedCard.querySelectorAll('.arrow-btn').forEach((button) => button.remove());
  // 视频替换为占位图（html-to-image 无法渲染 video）
  clonedCard.querySelectorAll('video').forEach((video) => {
    const imgElement = document.createElement('img');
    imgElement.src = '/assets/emoji-smiley-sweat.avif';
    imgElement.style.width = video.style.width;
    imgElement.style.height = video.style.height;
    imgElement.style.objectFit = 'contain';
    imgElement.className = video.className;
    video.parentNode.replaceChild(imgElement, video);
  });
  wrapperDiv.appendChild(clonedCard);

  // 把卡片内 Font Awesome 图标替换为内联 SVG（见文件顶部 replaceFontAwesomeIcons 注释），
  // 否则 skipFonts:true 会让这些字体图标在导出图里变空白方块（日期图标 / 分享按钮）。
  replaceFontAwesomeIcons(clonedCard);

  const footerDiv = document.createElement('div');
  footerDiv.style.marginTop = '10px';
  footerDiv.style.textAlign = 'center';

  const footerText = document.createElement('p');
  footerText.innerHTML = `
    <i class="fas fa-minus" style="transform: scale(2, 0.5); margin-right: 6px; opacity: 0.3;"></i>
    ${brandText}
    <i class="fas fa-minus" style="transform: scale(2, 0.5); margin-left: 6px; opacity: 0.3;"></i>`;
  footerText.style.fontSize = '12px';
  footerText.style.color = '#4b5563';
  footerDiv.appendChild(footerText);

  const additionalInfo = document.createElement('div');
  additionalInfo.innerHTML = domain;
  additionalInfo.style.fontSize = '12px';
  additionalInfo.style.color = '#6b7280';
  footerDiv.appendChild(additionalInfo);

  wrapperDiv.appendChild(footerDiv);
  return wrapperDiv;
}

// 生成并下载分享卡片 PNG
// options: { itemName, brandText, domain, width, downloadName, gtagDownload, gtagShareDownload, shareData }
export function generateShareCard(cardEl, options = {}) {
  const {
    itemName = '',
    brandText = 'AI灵感库',
    domain = SHARE_DOMAIN,
    width = 420,
    downloadName,
    gtagDownload = 'download',
    gtagShareDownload = 'share_download',
    shareData = null,
  } = options;

  const hasShare = !!(shareData && shareData.message && shareData.message.trim() !== '');

  let wrapperDiv = createCardWrapper(cardEl, { width, brandText, domain });
  if (hasShare) {
    buildShareInfo(wrapperDiv, shareData);
  }

  document.body.appendChild(wrapperDiv);

  // 返回 Promise：调用方可据此驱动「生成中」loading 态（resolve=下载已触发，reject=出错）
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      htmlToImage
        .toPng(wrapperDiv, {
          // 用 pixelRatio 提升导出分辨率到 2×（视网膜清晰度），不再用 transform 放大。
          // 这样 wrapper 的 layout 保持 420×H，border-radius 在 layout 边界内四角都生效，
          // 不会出现「仅左上圆角、其余直角」的裁切 bug（见文件头注释）。
          pixelRatio: 2,
          // 跳过 html-to-image 的 Web 字体内联：它会遍历 document.styleSheets 并读取
          // 跨域 Google Fonts（fonts.googleapis.com）的 cssRules，因缺少 CORS 头而抛出
          // SecurityError，导致整张卡片截图失败、下载无反应。skipFonts 可彻底绕开该路径，
          // 卡片文案会回退为系统衬线字体（与 Noto Serif SC 同为 serif，视觉差异极小）。
          skipFonts: true,
        })
        .then((dataUrl) => {
          const link = document.createElement('a');
          link.download = downloadName || `${brandText}-${itemName}.png`;
          link.href = dataUrl;
          link.click();

          if (window.gtag) {
            window.gtag('event', hasShare ? gtagShareDownload : gtagDownload, {
              event_category: hasShare ? 'Card_share Download' : 'Card Download',
              event_label: itemName,
              value: 1,
            });
          }

          if (document.body.contains(wrapperDiv)) {
            document.body.removeChild(wrapperDiv);
          }
          resolve();
        })
        .catch((error) => {
          console.error('Error generating image:', error);
          if (document.body.contains(wrapperDiv)) {
            document.body.removeChild(wrapperDiv);
          }
          reject(error);
        });
    }, 400);
  });
}